import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowUpRight, Home, MessageCircle, Phone, Search } from "lucide-react";
import SEO from "../seo/SEO";
import { WHATSAPP_URL, PHONE_TEL, PHONE_DISPLAY } from "../data/site";

const POPULAR = [
  { label: "Book an Appointment", to: "/contact" },
  { label: "About Dr. Indresh Dixit", to: "/about" },
  { label: "Symptoms A–Z", to: "/symptoms" },
  { label: "Conditions Treated", to: "/diseases" },
  { label: "Procedures & Endoscopy", to: "/procedures" },
  { label: "Video Knowledge Center", to: "/videos" },
];

export default function NotFound() {
  // Signal a soft-404 for server-aware crawlers (Google respects meta robots noindex)
  useEffect(() => {
    // Set a runtime prerender status hint if a prerender service is in use.
    const meta = document.createElement("meta");
    meta.name = "prerender-status-code";
    meta.content = "404";
    document.head.appendChild(meta);
    return () => {
      meta.parentNode && meta.parentNode.removeChild(meta);
    };
  }, []);

  return (
    <main data-testid="not-found-page" className="min-h-[70vh] flex flex-col">
      <SEO
        title="Page Not Found (404) — Dr. Indresh Dixit, Gastroenterologist, Varanasi"
        description="The page you were looking for could not be found. Return to the homepage or search by symptom, condition or procedure."
        canonical="/404"
        noindex
        schemas={[]}
      />

      <section className="section-pad flex-1 flex items-center">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="overline">Error · 404</div>
            <h1
              data-testid="notfound-h1"
              className="font-serif-display mt-5 text-6xl sm:text-7xl lg:text-[104px] leading-[0.95] tracking-tight"
            >
              This page{" "}
              <span className="text-[#0370AD]">doesn&apos;t exist.</span>
            </h1>
            <p className="mt-7 text-black/70 max-w-xl">
              The link you followed may be old, mistyped, or the page may have
              been moved. Nothing you did wrong — let us help you find what you
              need.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/"
                data-testid="notfound-home-btn"
                className="btn-primary"
              >
                <Home className="h-4 w-4" />
                Back to Homepage
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="notfound-whatsapp-btn"
                className="btn-ghost"
              >
                <MessageCircle className="h-4 w-4" />
                Book on WhatsApp
              </a>
              <a
                href={PHONE_TEL}
                data-testid="notfound-call-btn"
                className="btn-ghost"
              >
                <Phone className="h-4 w-4" />
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <aside
              data-testid="notfound-popular"
              className="border border-black/10 rounded-[6px] bg-[#fbfaf7] p-7"
            >
              <div className="flex items-center gap-2 overline">
                <Search className="h-3.5 w-3.5" />
                Popular Sections
              </div>
              <h2 className="font-display-semibold mt-4 text-2xl md:text-3xl leading-tight">
                Try one of these instead.
              </h2>
              <ul className="mt-6 divide-y divide-black/10">
                {POPULAR.map((p) => (
                  <li key={p.to}>
                    <Link
                      to={p.to}
                      data-testid={`notfound-link-${p.to.replace(/\//g, "-").replace(/^-/, "")}`}
                      className="group flex items-center justify-between py-3 hover:text-[#0370AD] transition-colors"
                    >
                      <span className="font-medium">{p.label}</span>
                      <ArrowUpRight className="h-4 w-4 arrow text-black/40 group-hover:text-[#0370AD]" />
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
